import { redirect } from "react-router";

import { clubhouseActions } from "../features/clubhouse/clubhouseSlice";
import { globalActions, selectSetting } from "../features/global/globalSlice";
import { userActions } from "../features/user/userSlice";
import { getTeam } from "../lib/api";
import { canEditTeam } from "../lib/utils";
import { ITeam } from "../types/api";
import { ILocalStorageUserData } from "../types/users";

import { store } from "..";

const setHeader = (currentTeam: ITeam, page: string, request: Request) => {
    const pageHeaders: {
        [key: string]: string | (() => string);
    } = {
        schedule: () => {
            let header = `Schedule`;
            const subPage = request.url.match(/schedule\/([^\/]+)(\/|$)/);

            if (subPage) {
                switch (subPage[1]) {
                    case "create-game":
                        header = `Schedule Game`;
                        break;
                    case "reschedule-game":
                        header = `Reschedule Game`;
                        break;
                    case "game":
                        const gamePage =
                            request.url.match(/\/game\/\d+\/(.*)$/);

                        if (gamePage) {
                            switch (gamePage[1]) {
                                case "logs":
                                    header = "Game Logs";
                                    break;
                                case "summary":
                                    header = "Game Summary";
                                    break;
                            }
                        }
                        break;
                }
            }

            return header;
        },
        roster: () => {
            let playerAction: string = "";
            const isOnRosterPage = /\/roster\/?$/.test(request.url);

            if (!isOnRosterPage) {
                playerAction = /team\/\d+\/roster\/add\/?$/.test(request.url)
                    ? "Add"
                    : "Edit";
            }
            return isOnRosterPage ? "Roster" : `${playerAction} Player`;
        },
        coi: "COI",
        "policies-procedures": "Policies & Procedures",
        ratings: "Team Ratings",
        "pitch-counts": `Pitch Counts`,
        placement: `Placement`,
        registrations: "Tournament Registrations",
    };

    return typeof pageHeaders[page] === "function"
        ? (pageHeaders[page] as () => string)()
        : pageHeaders[page];
};

// const canViewTeam = () => {
//     const user = store.getState().auth.user;
//     const league = store.getState().clubhouse.league;

//     if (!user) {
//         return false;
//     } else {
//         if (user?.userLevel > 5) {
//             return false;
//         }

//         if (
//             league?.teams.some(team => team.coach?.id === user.id) ||
//             league?.teams.some(
//                 team => team.organization?.primaryUser === user.id,
//             )
//         ) {
//             return true;
//         }
//     }
//     return false;
// };

export const loader = async ({
    params,
    request,
}: {
    params: any;
    request: Request;
}) => {
    try {
        const user = store.getState().auth
            .user as ILocalStorageUserData["user"];
        const teamId = params.teamId;
        const url = new URL(request.url);
        const match = url.pathname.match(
            /\/coachs-clubhouse\/team\/\d+\/([^/]+)/,
        );
        const pageName: string | null = match ? match[1] : null;

        let teams = store.getState().clubhouse.teams;
        const clubhouseTournaments = store.getState().clubhouse.tournaments;
        const umpires = store.getState().global.umpires;
        const ageLevels = store.getState().global.ageLevels;
        const users = store.getState().user.users;
        const settings = store.getState().global.settings;

        const clubhouseTeamsPromise =
            user.userLevel <= 2
                ? clubhouseTournaments.length
                    ? Promise.resolve()
                    : store.dispatch(clubhouseActions.getTournaments(user.id))
                : teams.length
                  ? Promise.resolve()
                  : store.dispatch(
                        clubhouseActions.getTeams({ userId: user.id }),
                    );

        const ageLevelsPromise = ageLevels.length
            ? Promise.resolve()
            : store.dispatch(globalActions.getAgeLevels());

        const usersPromise = users.length
            ? Promise.resolve()
            : store.dispatch(userActions.getUsers());

        const umpiresPromise = umpires.length
            ? Promise.resolve()
            : store.dispatch(globalActions.getUmpires());

        const settingsPromise = settings.length
            ? Promise.resolve()
            : store.dispatch(globalActions.getSettings());

        await Promise.all([
            clubhouseTeamsPromise,
            ageLevelsPromise,
            umpiresPromise,
            usersPromise,
            settingsPromise,
        ]);

        teams = store.getState().clubhouse.teams;

        const currentTeam: ITeam =
            teams.find(team => team.id === teamId) ||
            (await getTeam(teamId)).team;

        if (canEditTeam(currentTeam)) {
            console.log("Can Edit Team");

            store.dispatch(clubhouseActions.setSelectedTeam(currentTeam));
        } else {
            console.log("Can't Edit or View Team");
            return redirect("/coachs-clubhouse");
        }

        const currentYear = selectSetting(
            store.getState(),
            "wibb_current_league_year",
        );
        const currentSeason = selectSetting(
            store.getState(),
            "wibb_current_league_season",
        );

        const currentAgeLevel = currentTeam.ageLevels.find(
            t =>
                t.season.season === currentSeason?.setting &&
                t.year === parseInt(currentYear?.setting),
        );

        const teamAgeLevel = currentTeam.ageLevels.reduce((prev, current) => {
            return new Date(current.year, current.season.id) >
                new Date(prev.year, prev.season.id)
                ? current
                : prev;
        }, currentTeam.ageLevels[0]);

        const ageLevel = currentAgeLevel || teamAgeLevel;

        const header =
            currentTeam &&
            (pageName
                ? `${currentTeam.name} ${
                      ageLevel ? `(${ageLevel.ageLevel})` : ""
                  } / ${setHeader(currentTeam, pageName, request)}`
                : `${currentTeam.name} ${
                      ageLevel ? `(${ageLevel.ageLevel})` : ""
                  }`);

        return {
            teamId: params.teamId,
            header,
        };
    } catch (e: any) {
        return {
            teamId: params.teamId,
            header: "",
        };
    }
};
